import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Card, Stack,
  Avatar, Divider, TextField,IconButton
} from "@mui/material";
import { 
  FileText, Download, TrendingUp, TrendingDown, 
  Minus, Upload, CheckCircle2, AlertCircle 
} from "lucide-react";
import { toast } from "react-toastify";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = {
  aqua: "#00C0CA",
  dark: "#22404D",
  red: "#F65F4E",
  slate: "#94A3B8",
  emerald: "#10B981"
};

const VerRendicion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [archivos, setArchivos] = useState([]);
  const [adelanto, setAdelanto] = useState(null);
  const [montoAprobado, setMontoAprobado] = useState(0);
  const [archivo, setArchivo] = useState(null);

  useEffect(() => {
    const fetchRendicion = async () => {
      try {
        const response = await fetch(`${apiUrl}/adelanto/verRendicion?idAdelanto=${id}`, {
          headers: {
            "Content-Type": "application/json",
            "apiKey": localStorage.getItem("apiKey"),
          },
        });
        if (!response.ok) throw new Error("Error al obtener los datos");
        const data = await response.json();
        setAdelanto(data);
        if (!data.laRendicion) throw new Error("No hay rendición disponible");

        setMontoAprobado(data.importe || 0);
        setArchivos(
          data.laRendicion.itemsRendicionDTOs.map(item => ({
            nombre: item.nombreArchivo,
            rubro: item.rubro || "Sin rubro",
            proveedor: item.proveedor || "Desconocido",
            monto: item.monto || 0,
            url: item.link || "#",
          }))
        );
      } catch (error) {
        console.error("Error fetching rendición:", error);
        navigate("/");
      }
    };
    fetchRendicion();
  }, [id, navigate]);

  const totalMonto = archivos.reduce((sum, a) => sum + a.monto, 0);
  const diferencia = montoAprobado - totalMonto;
  const colorDiferencia = diferencia === 0 ? PALETTE.emerald : PALETTE.red;

  const handleRedirect = (link) => window.open(link, "_blank");
  const cambioElArchivo = (e) => setArchivo(e.target.files[0]);

  const terminarRendicion = async () => {
    try {
      const formData = new FormData();
      if (diferencia === 0) {
        const blob = new Blob(["Comprobante generado automáticamente"], { type: "text/plain" });
        formData.append("File", blob, "comprobante_Autogenerado.txt");
      } else {
        if (!archivo) {
          toast.error("Por favor, selecciona un archivo.");
          return;
        }
        formData.append("File", archivo);
      }

      const response = await fetch(`${apiUrl}/Adelanto/terminarRendicionUsuario?id=${adelanto.id}`, {
        method: "PUT",
        headers: { apiKey: localStorage.getItem("apiKey") },
        body: formData,
      });

      if (response.ok) {
        setAdelanto(prev => ({
          ...prev,
          laRendicion: { ...prev.laRendicion, rendicioUsuario: true, mensaje: "Rendición pendiente de aprobación" },
        }));
        toast.success("Rendición enviada exitosamente.");
      } else {
        const errorRes = await response.json();
        toast.error(`Error: ${errorRes.message}`);
      }
    } catch {
      toast.error("Error al actualizar la rendición.");
    }
  };

  const ResumenCard = ({ title, value, color, icon: Icon }) => (
    <Card elevation={0} sx={{ p: 2.5, borderRadius: '24px', border: '1px solid rgba(0,0,0,0.05)', mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar sx={{ bgcolor: `${color}15`, color: color, borderRadius: '12px' }}>
          <Icon size={20} />
        </Avatar>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 800, color: PALETTE.slate, textTransform: 'uppercase' }}>{title}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, color: PALETTE.dark }}>${value.toLocaleString()}</Typography>
        </Box>
      </Stack>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 5 }, mt: "64px", bgcolor: "#F4F7F9", minHeight: "100vh" }}>
      <Typography variant="h4" sx={{ fontWeight: 900, color: PALETTE.dark, mb: 4 }}>
        Resumen de Rendición
      </Typography>

      <Grid container spacing={4}>
        {/* LISTADO DE COMPROBANTES */}
        <Grid item xs={12} md={8}>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '32px', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <Table>
              <TableHead sx={{ bgcolor: PALETTE.dark }}>
                <TableRow>
                  {["Documento", "Rubro", "Proveedor", "Monto", "Acción"].map((head) => (
                    <TableCell key={head} sx={{ color: 'white', fontWeight: 800, py: 2 }}>{head}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {archivos.length > 0 ? archivos.map((a, i) => (
                  <TableRow key={i} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                    <TableCell sx={{ fontWeight: 700, color: PALETTE.dark }}>{a.nombre}</TableCell>
                    <TableCell sx={{ color: PALETTE.slate, fontWeight: 600 }}>{a.rubro}</TableCell>
                    <TableCell sx={{ color: PALETTE.slate, fontWeight: 600 }}>{a.proveedor}</TableCell>
                    <TableCell sx={{ fontWeight: 900, color: PALETTE.dark }}>${a.monto.toLocaleString()}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleRedirect(a.url)} sx={{ color: PALETTE.aqua, bgcolor: `${PALETTE.aqua}10` }}>
                        <Download size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: PALETTE.slate }}>No hay comprobantes cargados.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* SIDEBAR DE ESTADO Y ACCIONES */}
        <Grid item xs={12} md={4}>
          <ResumenCard title="Monto Aprobado" value={montoAprobado} color={PALETTE.aqua} icon={TrendingUp} />
          <ResumenCard title="Total Rendido" value={totalMonto} color={PALETTE.dark} icon={FileText} />
          <ResumenCard title="Diferencia" value={diferencia} color={colorDiferencia} icon={diferencia === 0 ? CheckCircle2 : Minus} />

          {/* MENSAJES DINÁMICOS POR ROL */}
          {diferencia < 0 && (
            <Box sx={{ p: 2, borderRadius: '20px', bgcolor: `${PALETTE.red}10`, border: `1px dashed ${PALETTE.red}`, mb: 2 }}>
              <Typography variant="body2" sx={{ color: PALETTE.red, fontWeight: 800 }}>
                {localStorage.getItem("rol") === "coordinador" 
                  ? `Aguarde el reintegro de $${(diferencia * -1).toLocaleString()} por parte de Finanzas.`
                  : `Debe adjuntar comprobante de transferencia por $${(diferencia * -1).toLocaleString()}.`}
              </Typography>
            </Box>
          )}

          {/* INPUT DE ARCHIVO */}
          {((diferencia > 0 && localStorage.getItem("rol") === "coordinador") || 
            (diferencia < 0 && localStorage.getItem("rol") === "finanzas")) && 
            !adelanto?.laRendicion?.rendicioUsuario && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', border: '1px solid #E0E0E0', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Upload size={18} /> Subir Comprobante de Resto
              </Typography>
              <TextField type="file" fullWidth onChange={cambioElArchivo} size="small" variant="outlined" InputLabelProps={{ shrink: true }} />
            </Paper>
          )}

          {/* COMPROBANTE DE RESTO YA SUBIDO */}
          {adelanto?.laRendicion?.rendicioUsuario && diferencia !== 0 && (
            <Button 
              fullWidth 
              variant="outlined" 
              startIcon={<FileText />}
              onClick={() => handleRedirect(adelanto.laRendicion.linkFacturaResto)}
              sx={{ borderRadius: '16px', fontWeight: 800, mb: 2 }}
            >
              Ver Comprobante de Resto
            </Button>
          )}

          {/* BOTÓN FINALIZAR */}
          {adelanto?.estado === "Aprobado" && !adelanto?.laRendicion?.rendicioUsuario && (
            ((localStorage.getItem("rol") === "finanzas" && diferencia < 0) || 
             (localStorage.getItem("rol") === "coordinador")) && (
              <Button 
                fullWidth 
                variant="contained" 
                size="large"
                onClick={terminarRendicion}
                sx={{ 
                  bgcolor: PALETTE.red, 
                  borderRadius: '20px', 
                  fontWeight: 900, 
                  py: 2,
                  boxShadow: `0 10px 20px ${PALETTE.red}40`,
                  '&:hover': { bgcolor: '#D32F2F' }
                }}
              >
                Finalizar Rendición
              </Button>
            )
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default VerRendicion;